import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sun, CloudSun, Cloud, CloudLightning, ArrowClockwise, CircleNotch,
  Plus, Receipt, Calculator, CalendarCheck, ArrowRight, Compass, TrendDown, WarningCircle,
} from "@phosphor-icons/react";

const money0 = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return s; } };

const WEATHER = {
  sunny: { Icon: Sun, label: "Sunny", color: "#16A34A", bg: "var(--bk-success-bg)", tag: "Strong & healthy" },
  "partly-cloudy": { Icon: CloudSun, label: "Partly cloudy", color: "#B5761F", bg: "#FBF3E2", tag: "Doing well, watch a few things" },
  cloudy: { Icon: Cloud, label: "Cloudy", color: "#8A6A3B", bg: "#F3EFE7", tag: "Needs attention" },
  stormy: { Icon: CloudLightning, label: "Stormy", color: "#B23B1F", bg: "#FDECE8", tag: "Act now to steady the ship" },
};

const PRIORITY = {
  high: { color: "#B23B1F", bg: "#FDECE8", label: "Now" },
  medium: { color: "#B5761F", bg: "#FBF3E2", label: "Soon" },
  low: { color: "var(--bk-text-2)", bg: "var(--bk-page)", label: "Later" },
};

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const vals = points.map((p) => p.balance);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const W = 260, H = 48;
  const step = W / (points.length - 1);
  const y = (v) => H - ((v - min) / range) * (H - 6) - 3;
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const zeroInRange = min <= 0 && max >= 0;
  const negative = vals.some((v) => v <= 0);
  const stroke = negative ? "#B23B1F" : "#16A34A";
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
      {zeroInRange && <line x1="0" x2={W} y1={y(0)} y2={y(0)} stroke="#CBD5E1" strokeDasharray="3 3" strokeWidth="1" />}
      <path d={path} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function CommandCenter({ refreshKey }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/command-center").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const link = (area, bid) => navigate(`/business/${bid || data?.action_business_id}?tab=${area}`);

  if (loading && !data) {
    return (
      <div className="bk-card p-8 mb-10 flex items-center gap-3" data-testid="command-center-loading">
        <CircleNotch size={22} className="animate-spin" color="var(--bk-accent)" />
        <span className="text-sm" style={{ color: "var(--bk-text-2)" }}>Reading your books, taxes & inventory…</span>
      </div>
    );
  }
  if (!data || !data.has_data) return null;

  const w = WEATHER[data.weather] || WEATHER.cloudy;
  const WIcon = w.Icon;
  const p = data.predictive;
  const actions = [
    { label: "Add income / expense", Icon: Plus, area: "books" },
    { label: "Create invoice", Icon: Receipt, area: "invoices" },
    { label: "Scan receipt", Icon: Receipt, area: "receipts" },
    { label: "Build estimate", Icon: Calculator, area: "jobs" },
    { label: "Log a bill", Icon: CalendarCheck, area: "scheduler" },
  ];

  return (
    <section className="mb-10" data-testid="command-center">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Compass size={22} weight="duotone" color="var(--bk-gold)" />
          <h2 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>CEO Command Center</h2>
        </div>
        <button onClick={load} data-testid="cc-refresh" className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md hover:bg-[#F5F5F0] transition-colors" style={{ color: "var(--bk-text-2)" }}>
          <ArrowClockwise size={15} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Action Command Bar */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="action-command-bar">
        {actions.map((a) => (
          <button key={a.area} onClick={() => link(a.area)} data-testid={`cc-action-${a.area}`}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full text-sm font-semibold border transition-all hover:-translate-y-0.5 hover:shadow-sm bg-white"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            <a.Icon size={16} weight="bold" color="var(--bk-accent)" /> {a.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Health Score */}
        <div className="p-6 rounded-[24px] text-white shadow-[0_12px_36px_rgba(26,39,68,0.18)]" style={{ background: "var(--bk-gradient-primary)" }} data-testid="health-score-card">
          <div className="flex items-center gap-4">
            <ScoreRing score={data.health_score} color={w.color} />
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: "rgba(255,255,255,0.16)", color: "#fff" }} data-testid="cc-weather">
                <WIcon size={15} weight="fill" /> {w.label}
              </div>
              <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.76)" }}>{w.tag}</p>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            {data.score_breakdown.map((b) => (
              <div key={b.label}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span style={{ color: "rgba(255,255,255,0.74)" }}>{b.label}</span>
                  <span className="font-semibold tabular-nums text-white">{b.points}/{b.max}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.18)" }}>
                  <div className="h-1.5 rounded-full" style={{ width: `${(b.points / b.max) * 100}%`, background: w.color, transition: "width .5s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Predictive Future runway */}
        <div className="bk-card p-6 flex flex-col bk-lift" data-testid="predictive-runway-card">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>Predictive cash runway · next 90 days</p>
            {p.out_of_cash_date
              ? <WarningCircle size={18} weight="fill" color="#B23B1F" />
              : <TrendDown size={18} weight="duotone" color="var(--bk-success)" style={{ transform: "scaleY(-1)" }} />}
          </div>
          <div className="mt-3">
            {p.cash_flow_positive ? (
              <p className="font-display text-2xl font-bold" style={{ color: "var(--bk-success)" }} data-testid="cc-runway-headline">Cash-flow positive</p>
            ) : p.out_of_cash_date ? (
              <p className="font-display text-2xl font-bold" style={{ color: "#B23B1F" }} data-testid="cc-runway-headline">Out of cash ~{fmtDate(p.out_of_cash_date)}</p>
            ) : (
              <p className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="cc-runway-headline">90+ days of runway</p>
            )}
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
              Low point <span className="font-semibold" style={{ color: p.lowest_balance < 0 ? "#B23B1F" : "var(--bk-text)" }}>{money0(p.lowest_balance)}</span> on {fmtDate(p.lowest_date)}
            </p>
          </div>
          <div className="mt-auto pt-4">
            <Sparkline points={p.points} />
            <button onClick={() => link("forecast")} data-testid="cc-view-forecast" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--bk-accent)" }}>
              View full forecast <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* CEO Directives */}
        <div className="bk-rowan-card p-6" data-testid="ceo-directives">
          <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: "var(--bk-text-2)" }}>Your 5 CEO directives</p>
          <div className="space-y-2">
            {data.directives.map((d, i) => {
              const pr = PRIORITY[d.priority] || PRIORITY.medium;
              return (
                <button key={i} onClick={() => link(d.area, d.business_id)} data-testid={`cc-directive-${i}`}
                  className="w-full text-left rounded-lg p-3 flex items-start gap-3 transition-all hover:-translate-y-0.5 hover:shadow-sm border"
                  style={{ borderColor: "var(--bk-border)", background: "white" }}>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full shrink-0 mt-0.5" style={{ background: pr.bg, color: pr.color }}>{pr.label}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold truncate" style={{ color: "var(--bk-text)" }}>{d.title}</span>
                    <span className="block text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{d.detail}</span>
                  </span>
                  <ArrowRight size={15} weight="bold" className="shrink-0 mt-1" style={{ color: "var(--bk-accent)" }} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function ScoreRing({ score, color }) {
  const r = 34, c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: 84, height: 84 }}>
      <svg width="84" height="84" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="42" cy="42" r={r} fill="none" stroke="var(--bk-page)" strokeWidth="7" />
        <circle cx="42" cy="42" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .7s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-bold leading-none" style={{ color: "var(--bk-text)" }} data-testid="cc-health-score">{score}</span>
        <span className="text-[10px]" style={{ color: "var(--bk-text-2)" }}>/ 100</span>
      </div>
    </div>
  );
}
